export const VOTER_REGISTRATION_TEMPLATE = `# Closing {{deceased_first_name}}'s voter record

This guide helps you check whether {{deceased_first_name}}'s voter registration is still active and, if needed, ask the correct election office to cancel it. This helps keep public records accurate and reduces the chance of election mail or ballot materials being sent in their name.

Voter registration is handled at the state or county level, so the exact process can vary. The election office will tell you what they need.

# Steps to Take

## Check the voter registration status

Use the official USA.gov voter registration status page: [How to confirm your voter registration status | USAGov](https://www.usa.gov/confirm-voter-registration)

You might see the status listed as 'Inactive.' This usually just means the record is already on its way to being cleared, but it takes time. If you want it handled right away, a manual request is the best way to go.

This allows you to select your state to access their specific lookup tool.

**If the registration is no longer active**
- Check the status again in 3-4 weeks to confirm the update has been processed.
- Save a screenshot or write down the date you checked.

**If the registration is still active**
- Contact the election office for the county or state where {{deceased_first_name}} was registered.
- Use the official USA.gov election office directory: [Find your state or local election office](https://www.usa.gov/election-office)
- This directory helps you find the correct state or local election office.

## Ask what they need to cancel the registration

Use this script when calling:

> "Hi, I am calling to update the voter rolls because my relationship (e.g., spouse, child, or executor), {{deceased_first_name}}, has passed away."

> "Can you tell me what specific documents your office may need to close this registration?"

## Sharing the details with the office

Follow the election office's instructions. They may allow you to send the request using one of these methods:
- Send the request by mail.
- Send via email or fax.
- Complete an online form.
- Return voter mail with a "Deceased" note.
- Save copies of anything you send.

{{#if state_GA}}
# {{deceased_first_name}} was registered in Georgia

**Have these ready:**
- {{deceased_first_name}}'s GA Driver's License or the last 4 digits of their Social Security number.
- Date of birth and county of registration.

Georgia has a dedicated voter registration cancellation process. The Georgia Secretary of State says a voter or a family member of a deceased voter can request cancellation, including through the Voter Cancellation Request Portal. If you do not have these details, contact the county election office to ask for the physical paper cancellation form.

Use Georgia's official page: [Voter Registration Cancellation | Georgia Secretary of State](https://sos.ga.gov/voter-registration-cancellation)
{{/if}}
{{#if state_TX}}
# {{deceased_first_name}} was registered in Texas

In Texas, voter registration records are handled by the local county Voter Registrar. If you received {{deceased_first_name}}'s voter registration certificate, you can return it to the Voter Registrar.

On the front of the certificate, write:
- **Deceased** (Write 'Deceased' clearly. You may also notify the post office, but a direct mailing to the Voter Registrar is the primary method for requesting an immediate cancellation.).

On the back (or front), sign your name and clearly state your relationship to the deceased (e.g., 'Daughter' or 'Executor').

Then return the certificate to the county Voter Registrar.

You can find the correct Texas county Voter Registrar here: [Texas County Voter Registration Officials](https://www.sos.state.tx.us/elections/voter/votregduties.shtml)

If you do not have the certificate, send a signed letter explaining the situation to the Voter Registrar. The statement must be signed by a close family member, such as a spouse, parent, child, sibling, or grandparent. Include the voter's full name, registration address, and date of birth. Alternatively, you may send a certified copy of the death certificate.

If you are not a spouse, parent, child, sibling, or grandparent, contact a legal professional or the Voter Registrar to confirm your authority to sign.
{{/if}}

# Quick Checklist

- [ ] Check the voter registration status
- [ ] Save a screenshot or note the date checked
- [ ] If still active, find the correct state or county election office
- [ ] Ask what they need to cancel a deceased person's registration
- [ ] Send the death certificate or form, if requested
- [ ] Save copies of anything you send
- [ ] Follow up if the registration still appears active after a few weeks`;
